/**
 * The board (`KLQ-20260821-001` §C) — a status column per status the filter admits, over the
 * same filter object, with a drag gesture on top, and no new fetch thinking.
 *
 * FOUR DECISIONS THIS FILE IS BUILT ON
 *
 * 1. **One independent list per rendered column, one filter object.** Each column is the
 *    register's own query with `status` pinned — `{ ...filters, status }` into `useTaskList`,
 *    so a column inherits every narrowing the bar sets and adds exactly one. A column
 *    paginates on its own; a column that is empty says so on its own. The bar's own status
 *    set still writes the URL — the register reads it, and a pasted link keeps it — but each
 *    column's pinned status wins *within* the column, because a board with one populated
 *    column is a register wearing a costume. Which columns exist at all is the bar's set's
 *    call, not this file's (`columns` below).
 * 2. **The client decides no workflow.** What may be dragged where is the dragged row's
 *    `allowed_transitions` — computed per caller by the server, same pattern as the reviews
 *    screen's `allowed_decisions`. A column outside that list simply does not accept the
 *    drop; there is no transition graph here to disagree with the server's on the day the
 *    gate changes (§F). A card whose list is empty is not draggable at all.
 * 3. **Optimism is bounded, and it is render state.** The dropped card is drawn in its target
 *    column immediately, from this component's own `pending` state — never a cache write, so
 *    no optimistic write can invent a `closed_at`, a `resolved_by_version_id` or a number.
 *    On failure the state clears, the card is back where the server says it is, and the
 *    server's own sentence — the illegal jump, or the verification gate naming who has to
 *    close — renders at the card (`problems.ts:detailOf`).
 * 4. **Native drag, no library.** The same reasoning as the register's missing virtualisation
 *    dependency: the HTML5 events do the job, and a drag-and-drop package is measurement code
 *    this screen does not need for at most four columns and bounded rows.
 *
 * Column headers count through `LoadedCount` — §D's `+` rule has one printer, and a bare
 * `{rows.length}` here would be the call site that forgets it.
 */

import type { CSSProperties } from 'react';
import { useState } from 'react';

import { Button, InlineError, Skeleton } from '@/components';
import { relativeTime } from '@/features/projects';
import type { MessageKeys, Translate } from '@/lib/i18n';
import { useLocale, useMessages } from '@/lib/i18n';
import { useInfiniteSentinel } from '@/lib/infinite';

import type { Task, TaskStatus } from './api';
import { boardColumns } from './filters';
import type { TaskFilters } from './filters';
import { KIND_KEY } from './kinds';
import { LoadedCount } from './LoadedCount';
import { detailOf, taskErrorKey } from './problems';
import { renderPersonNames, useMemberNames, useMoveTask, useTaskList } from './queries';
import { KindGlyph } from './TaskRegister';
import styles from './Tasks.module.css';

type Key = MessageKeys['tasks'];

const STATUS_KEY: Readonly<Record<TaskStatus, Key>> = {
  open: 'status.open',
  in_progress: 'status.inProgress',
  done: 'status.done',
  closed: 'status.closed',
};

/** The card in flight: what left, from where. Cleared the moment the pointer lets go. */
interface Drag {
  readonly task: Task;
  readonly from: TaskStatus;
}

/** The card the server has not answered about yet: decision 3's whole state. */
interface PendingMove extends Drag {
  readonly to: TaskStatus;
}

/** The server's refusal, pinned to the card it refused so it renders where the row was dropped. */
interface Refusal {
  readonly taskId: string;
  readonly message: string;
}

export interface TaskBoardProps {
  readonly projectId: string;
  readonly filters: TaskFilters;
}

export function TaskBoard({ projectId, filters }: TaskBoardProps) {
  const t = useMessages('tasks');
  const { locale } = useLocale();
  const nameOf = useMemberNames(projectId);
  const move = useMoveTask(projectId);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [pending, setPending] = useState<PendingMove | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  // The bar's status set decides which columns exist (`KLQ-20260825-003` §H). The rule itself
  // lives in `filters.boardColumns`, where a test can reach it — the choice was made, not
  // defaulted into: if hiding reads as *the board is broken* rather than *the board is
  // filtered*, the spec's Notes name the fallback — all four columns, the excluded ones
  // visibly empty and labelled as filtered out.
  const columns = boardColumns(filters.status);

  const drop = (to: TaskStatus): void => {
    // One move in flight at a time: `pending` is one card, and a second drop before the first
    // answer would need a queue the gesture does not earn. Dragging is disabled below anyway.
    if (drag === null || pending !== null) return;
    if (to === drag.from || !drag.task.allowed_transitions.includes(to)) return;
    const attempted: PendingMove = { ...drag, to };
    setDrag(null);
    setRefusal(null);
    setPending(attempted);
    move.mutate(
      { taskId: attempted.task.id, from: attempted.from, to, filters },
      {
        // Runs after the hook's own onSuccess has awaited the two columns' refetch, so the
        // optimistic placement is released only once the cache already agrees with it.
        onSuccess: () => {
          setPending(null);
        },
        onError: (error) => {
          setPending(null);
          setRefusal({
            taskId: attempted.task.id,
            message: detailOf(error) ?? t(taskErrorKey(error)),
          });
        },
      },
    );
  };

  return (
    <div className={styles.board}>
      {/* The screen's one bar is `TasksPage`'s, in `Screen`'s `toolbar` slot
          (`KLQ-20260910-001` §A). It reports no loaded count on this view: there is no single
          list to speak for, because every rendered column counts separately. */}

      {/* The one number the layout needs from the render: how many columns survived the
          filter. The track sizing itself stays in the module (`--board-columns`), so no
          geometry is spelled out here. */}
      <div
        className={styles.columns}
        style={{ '--board-columns': columns.length } as CSSProperties}
      >
        {columns.map((status) => (
          <BoardColumn
            key={status}
            projectId={projectId}
            filters={filters}
            status={status}
            drag={drag}
            pending={pending}
            refusal={refusal}
            dragDisabled={pending !== null}
            onDragStart={(task, from) => {
              setRefusal(null);
              setDrag({ task, from });
            }}
            onDragEnd={() => {
              setDrag(null);
            }}
            onDrop={drop}
            t={t}
            locale={locale}
            nameOf={nameOf}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One column: one status, one keyset query, its own pager and its own empty sentence.
 *
 * What it renders is the server's answer for its status, adjusted by at most one card — the
 * pending move, subtracted from the column it left and drawn atop the column it entered. The
 * position is presentation; every field on the card is the row the server already sent.
 */
function BoardColumn({
  projectId,
  filters,
  status,
  drag,
  pending,
  refusal,
  dragDisabled,
  onDragStart,
  onDragEnd,
  onDrop,
  t,
  locale,
  nameOf,
}: {
  readonly projectId: string;
  readonly filters: TaskFilters;
  readonly status: TaskStatus;
  readonly drag: Drag | null;
  readonly pending: PendingMove | null;
  readonly refusal: Refusal | null;
  readonly dragDisabled: boolean;
  readonly onDragStart: (task: Task, from: TaskStatus) => void;
  readonly onDragEnd: () => void;
  readonly onDrop: (to: TaskStatus) => void;
  readonly t: Translate<'tasks'>;
  readonly locale: string;
  readonly nameOf: ReturnType<typeof useMemberNames>;
}) {
  // The column **overrides** the bar's status set with exactly its own (`KLQ-20260825-003`
  // §H) — a column showing rows of another status is not a column.
  const list = useTaskList(projectId, { ...filters, status: [status] }, 'board');
  const [sentinel, setSentinel] = useState<HTMLLIElement | null>(null);
  useInfiniteSentinel(sentinel, {
    hasNextPage: list.hasNextPage,
    isFetchingNextPage: list.isFetchingNextPage,
    fetchNextPage: () => {
      void list.fetchNextPage();
    },
  });

  const fetched = list.data?.pages.flatMap((page) => page.items) ?? [];
  const rows =
    pending === null
      ? fetched
      : status === pending.from
        ? fetched.filter((task) => task.id !== pending.task.id)
        : status === pending.to
          ? [pending.task, ...fetched.filter((task) => task.id !== pending.task.id)]
          : fetched;
  const hasMore = list.data?.pages.at(-1)?.has_more ?? false;

  // Decision 2: the dragged row's own `allowed_transitions` is the whole answer to "may this
  // land here". The origin column is excluded — letting go there (or anywhere ineligible) is
  // a cancelled drag, and the server is never asked about a move to where the card already is.
  const eligible =
    drag !== null && status !== drag.from && drag.task.allowed_transitions.includes(status);

  return (
    <section
      className={[styles.column, eligible ? styles.columnEligible : '']
        .filter(Boolean)
        .join(' ')}
      aria-label={t(STATUS_KEY[status])}
      onDragOver={(event) => {
        if (eligible) {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
        }
      }}
      onDrop={(event) => {
        if (eligible) {
          event.preventDefault();
          onDrop(status);
        }
      }}
    >
      <header className={styles.columnHead}>
        <h3 className={styles.columnTitle}>{t(STATUS_KEY[status])}</h3>
        {list.isSuccess ? <LoadedCount count={rows.length} hasMore={hasMore} bare /> : null}
      </header>

      {list.isError ? (
        <InlineError variant="block">
          {detailOf(list.error) ?? t(taskErrorKey(list.error))}
        </InlineError>
      ) : list.isPending ? (
        // Two cards, not a column's worth. A card is taller than a list row, so four of them
        // fill the column — and a column drawn full that then says *nothing here* is the lie
        // the modest count exists to avoid. The bars are hidden and the list carries the
        // sentence, which is the same announcement the paragraph used to make.
        <ol
          className={styles.columnList}
          role="status"
          aria-busy="true"
          aria-label={t('status.loading')}
        >
          {[0, 1].map((card) => (
            <li key={card}>
              <article className={styles.card}>
                <Skeleton width={16} height={16} />
                <div className={[styles.rowBody, styles.loadingLines].join(' ')}>
                  <Skeleton width="90%" height={12} />
                  <Skeleton width="55%" height={12} />
                </div>
              </article>
            </li>
          ))}
        </ol>
      ) : rows.length === 0 ? (
        <p className={styles.muted}>{t('board.empty')}</p>
      ) : (
        <ol className={styles.columnList}>
          {rows.map((task) => (
            <li key={task.id}>
              <BoardCard
                task={task}
                t={t}
                locale={locale}
                assignee={renderPersonNames(t, nameOf, task.assignee_user_ids, t('row.unassigned'))}
                dragging={drag?.task.id === task.id}
                inFlight={pending?.task.id === task.id}
                draggable={
                  !dragDisabled && pending?.task.id !== task.id && task.allowed_transitions.length > 0
                }
                onDragStart={() => {
                  onDragStart(task, status);
                }}
                onDragEnd={onDragEnd}
              />
              {refusal?.taskId === task.id ? (
                // The server's own sentence, at the card it refused (§C): only the server
                // knows which rule said no and who has to close the verification gate.
                <InlineError variant="block">{refusal.message}</InlineError>
              ) : null}
            </li>
          ))}
          {/* **Inside the scrolling list, not under it.** One sentinel per column, because the
              columns scroll independently — but a sentinel parked below a column that does not
              scroll is permanently in view, and an observer that is permanently in view walks
              the whole cursor in one sitting. In here it is reached by scrolling *this* column,
              which is the gesture it is meant to answer. The button stays as the fallback
              (`lib/infinite.ts`). */}
          {list.hasNextPage ? (
            <li ref={setSentinel} className={styles.sentinel}>
              <Button
                size="sm"
                variant="secondary"
                disabled={list.isFetchingNextPage}
                onClick={() => {
                  void list.fetchNextPage();
                }}
              >
                {list.isFetchingNextPage ? t('list.loadingMore') : t('list.more')}
              </Button>
            </li>
          ) : null}
        </ol>
      )}


    </section>
  );
}

/**
 * One card: the register's row, stacked for a narrow column. Reads nothing the payload does
 * not carry (§G) — the assignee arrives already rendered, because the em dash has one printer.
 */
function BoardCard({
  task,
  t,
  locale,
  assignee,
  dragging,
  inFlight,
  draggable,
  onDragStart,
  onDragEnd,
}: {
  readonly task: Task;
  readonly t: Translate<'tasks'>;
  readonly locale: string;
  readonly assignee: string;
  readonly dragging: boolean;
  readonly inFlight: boolean;
  readonly draggable: boolean;
  readonly onDragStart: () => void;
  readonly onDragEnd: () => void;
}) {
  return (
    <article
      className={[
        styles.card,
        dragging || inFlight ? styles.cardLifted : '',
        draggable ? styles.cardDraggable : '',
      ]
        .filter(Boolean)
        .join(' ')}
      draggable={draggable}
      onDragStart={(event) => {
        // Nothing rides in the dataTransfer: the drag never leaves this board, and the state
        // it needs — the row, with its `allowed_transitions` — is richer than a string.
        event.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
    >
      <KindGlyph
        kind={task.kind}
        label={t(KIND_KEY[task.kind])}
        taskTypeId={task.task_type_id}
      />
      <div className={styles.rowBody}>
        <p className={styles.rowTitle}>
          <span className={styles.number}>{t('row.number', { number: task.number })}</span>{' '}
          {task.title}
        </p>
        <p className={styles.rowMeta}>
          {assignee}
          {task.due_at === null ? '' : ` · ${t('row.due', { when: relativeTime(task.due_at, locale) })}`}
        </p>
      </div>
    </article>
  );
}
